import React from 'react';
import { DeepMap } from 'react-hook-form';
import { SelectOptionElement, useDidUpdateEffect } from '@odin-labs/components';
import {
  FormInputTypes,
  TypedFormInputs,
  getUpdateInputValueFunction,
  GridColSpan,
  UseInputs,
  UseFormMethods,
} from 'components/form';
import { fillAddressDetails } from 'components/placesAutocomplete/utils';
import { ensureNonUndefinedFields } from 'utils';
import {
  toFancySelectOptions,
  statesOptions,
  genderOptions as genderOptionsValues,
  primaryLanguageOptions as primaryLanguageOptionsValues,
  raceOptions as raceOptionsValues,
  veteranOptions as veteranOptionsValues,
  tradeExperienceOptions as tradeExperienceOptionsValues,
} from 'utils/constants';
import { zipCodeValidation } from 'utils/validation';
import {
  generateCustomWorkerDataFieldInputs,
  getDefaultValueForCustomDataType,
} from 'utils/onboarding';
import { languageOptions } from 'containers/selfOnboarding/utils';
import {
  SelfOnboardingCustomWorkerDataField,
  SelfOnboardingJobsiteWorker,
  SelfOnboardingProfileFormData,
} from 'containers/selfOnboarding/types';
import {
  citizenshipStatusOptions,
  workAuthorizationStatusOptions,
} from 'containers/worker/helpers/utils';
import { MapMarkerAltIcon } from 'components/icons';
import {
  UpdateJobsiteWorkerMutationVariables,
  UpdateWorkerMutationVariables,
} from 'apollo/generated/client-operations';

export { statesOptions };
export const primaryLanguageOptions = toFancySelectOptions(
  primaryLanguageOptionsValues,
);
export const genderOptions = toFancySelectOptions(genderOptionsValues);
export const raceOptions = toFancySelectOptions(raceOptionsValues);
export const veteranOptions = toFancySelectOptions(veteranOptionsValues);
export const tradeExperienceOptions = toFancySelectOptions(
  tradeExperienceOptionsValues,
);

const getFormInputs = ({
  tradeOptions,
  jobTitleOptions,
  unionAffiliationOptions,
  classificationGroupOptions,
  jobsiteCustomWorkerDataFields,
}: {
  tradeOptions: SelectOptionElement[];
  jobTitleOptions: SelectOptionElement[];
  unionAffiliationOptions: SelectOptionElement[];
  classificationGroupOptions: SelectOptionElement[];
  jobsiteCustomWorkerDataFields: SelfOnboardingCustomWorkerDataField[];
}): TypedFormInputs<SelfOnboardingProfileFormData> => {
  return {
    addressLine1: {
      element: FormInputTypes.PlacesAutocomplete,
      label: 'Address',
      elementProps: {
        icon: MapMarkerAltIcon,
        onCommit: fillAddressDetails,
      },
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
    },
    addressLine2: {
      element: FormInputTypes.Field,
      label: 'Address line 2',
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
    },
    addressCity: {
      element: FormInputTypes.Field,
      label: 'City',
      layout: ({ visibleFields }): string[] => [
        GridColSpan.SpanFull,
        visibleFields.includes('addressZipCode')
          ? GridColSpan.SmSpan4
          : GridColSpan.SmSpan6,
      ],
    },
    addressState: {
      element: FormInputTypes.Select,
      label: 'State',
      elementProps: {
        placeholder: '',
        options: statesOptions,
      },
      layout: ({ visibleFields }): string[] => [
        GridColSpan.SpanFull,
        visibleFields.includes('addressZipCode')
          ? GridColSpan.SmSpan4
          : GridColSpan.SmSpan6,
      ],
    },
    addressZipCode: {
      element: FormInputTypes.Field,
      label: 'Zip code',
      elementProps: {
        fieldType: 'zipcode',
      },
      validation: { pattern: zipCodeValidation },
      layout: ({ visibleFields }): string[] => [
        GridColSpan.SpanFull,
        visibleFields.includes('addressState')
          ? GridColSpan.SmSpan4
          : GridColSpan.SmSpan6,
      ],
    },
    primaryLanguage: {
      element: FormInputTypes.Select,
      label: 'Primary language',
      elementProps: {
        options: primaryLanguageOptions,
      },
      validation: { required: true },
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
    },
    citizenshipStatus: {
      element: FormInputTypes.Select,
      label: 'Citizenship status',
      elementProps: {
        options: citizenshipStatusOptions,
      },
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
    },
    workAuthorizationStatus: {
      element: FormInputTypes.Select,
      label: 'Work authorization status',
      elementProps: {
        options: workAuthorizationStatusOptions,
      },
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
    },
    race: {
      element: FormInputTypes.Select,
      label: 'Race',
      elementProps: {
        options: raceOptions,
      },
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
    },
    gender: {
      element: FormInputTypes.Select,
      label: 'Gender',
      elementProps: {
        options: genderOptions,
      },
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
    },
    isVeteran: {
      element: FormInputTypes.Select,
      label: 'Veteran status',
      elementProps: {
        options: veteranOptions,
      },
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
    },
    trade: {
      element: FormInputTypes.Select,
      label: 'Trade',
      elementProps: {
        options: tradeOptions,
      },
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
    },
    tradeExperience: {
      element: FormInputTypes.Select,
      label: 'Trade experience',
      elementProps: {
        options: tradeExperienceOptions,
      },
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
    },
    jobTitle: {
      element: FormInputTypes.Select,
      label: 'Job title',
      elementProps: {
        options: jobTitleOptions,
      },
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
    },
    unionAffiliation: {
      element: FormInputTypes.Select,
      label: 'Union affiliation',
      elementProps: {
        isCreateable: false,
        options: unionAffiliationOptions,
      },
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
    },
    classificationGroup: {
      element: FormInputTypes.Select,
      label: 'Classification group',
      elementProps: {
        isCreateable: false,
        options: classificationGroupOptions,
      },
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
    },
    ...generateCustomWorkerDataFieldInputs(jobsiteCustomWorkerDataFields),
  };
};

const getPrimaryLanguage = (language: string): SelectOptionElement => {
  const languageName =
    languageOptions.find((opt) => opt.value === language).label ?? 'English';
  return (
    primaryLanguageOptions.find((opt) => languageName.startsWith(opt.value)) ??
    null
  );
};

export const getFormInputsHook =
  (args: {
    language: string;
    tradeOptions: SelectOptionElement[];
    jobTitleOptions: SelectOptionElement[];
    unionAffiliationOptions: SelectOptionElement[];
    classificationGroupOptions: SelectOptionElement[];
    jobsiteCustomWorkerDataFields: SelfOnboardingCustomWorkerDataField[];
  }): UseInputs<SelfOnboardingProfileFormData> =>
  ({
    setValue,
  }: UseFormMethods<SelfOnboardingProfileFormData>): TypedFormInputs<SelfOnboardingProfileFormData> => {
    const {
      language,
      tradeOptions,
      jobTitleOptions,
      unionAffiliationOptions,
      classificationGroupOptions,
      jobsiteCustomWorkerDataFields,
    } = args;

    useDidUpdateEffect(() => {
      const primaryLanguage = getPrimaryLanguage(language);
      setValue('primaryLanguage', primaryLanguage, { shouldDirty: true });
    }, [language]);

    return React.useMemo(
      () =>
        getFormInputs({
          tradeOptions,
          jobTitleOptions,
          unionAffiliationOptions,
          classificationGroupOptions,
          jobsiteCustomWorkerDataFields,
        }),
      [
        tradeOptions,
        jobTitleOptions,
        unionAffiliationOptions,
        classificationGroupOptions,
      ],
    );
  };

export const getDefaultValues = (
  profile: SelfOnboardingProfileFormData,
  language: string,
  jobsiteCustomWorkerDataFields: SelfOnboardingCustomWorkerDataField[],
  jobsiteWorker: SelfOnboardingJobsiteWorker,
): SelfOnboardingProfileFormData => {
  const {
    addressLine1,
    addressLine2,
    addressCity,
    addressState,
    addressZipCode,
    primaryLanguage,
    citizenshipStatus,
    workAuthorizationStatus,
    race,
    gender,
    isVeteran,
    trade,
    tradeExperience,
    jobTitle,
    unionAffiliation,
    classificationGroup,
  } = profile ?? {};

  const customWorkerData = jobsiteWorker?.customWorkerData ?? {};

  const dynamicDefaultValues = jobsiteCustomWorkerDataFields.reduce(
    (acc, customField) => ({
      ...acc,
      [customField.fieldLabel]:
        customWorkerData[customField.fieldLabel] ??
        getDefaultValueForCustomDataType(customField.fieldDataType),
    }),
    {},
  );

  return {
    addressLine1: addressLine1 ?? '',
    addressLine2: addressLine2 ?? '',
    addressCity: addressCity ?? '',
    addressState: addressState ?? null,
    addressZipCode: addressZipCode ?? '',
    primaryLanguage: primaryLanguage ?? getPrimaryLanguage(language),
    citizenshipStatus: citizenshipStatus ?? null,
    workAuthorizationStatus: workAuthorizationStatus ?? null,
    race: race ?? null,
    gender: gender ?? null,
    isVeteran: isVeteran ?? null,
    trade: trade ?? null,
    tradeExperience: tradeExperience ?? null,
    jobTitle: jobTitle ?? null,
    unionAffiliation: unionAffiliation ?? null,
    classificationGroup: classificationGroup ?? null,
    ...dynamicDefaultValues,
  };
};

export type SelfOnboardingProfileUpdateInput = {
  workerInput: Pick<
    Required<UpdateWorkerMutationVariables['workerInput']>,
    | 'addressLine1'
    | 'addressLine2'
    | 'addressCity'
    | 'addressState'
    | 'addressZipCode'
    | 'primaryLanguage'
    | 'citizenshipStatus'
    | 'workAuthorizationStatus'
    | 'race'
    | 'gender'
    | 'isVeteran'
  >;
  jobsiteWorkerInput: Pick<
    Required<UpdateJobsiteWorkerMutationVariables['jobsiteWorkerInput']>,
    | 'trade'
    | 'tradeExperience'
    | 'jobTitle'
    | 'unionAffiliation'
    | 'classificationGroup'
    | 'customWorkerData'
  >;
};

export const getUpdateInput = (
  data: SelfOnboardingProfileFormData,
  dirtyFields: DeepMap<SelfOnboardingProfileFormData, true>,
  jobsiteCustomWorkerDataFields: SelfOnboardingCustomWorkerDataField[],
): SelfOnboardingProfileUpdateInput => {
  const getUpdateInputValue = getUpdateInputValueFunction(data, dirtyFields);

  const isVeteranInputValue = dirtyFields.isVeteran
    ? data.isVeteran?.value === 'Veteran'
    : undefined;

  return {
    workerInput: ensureNonUndefinedFields<
      SelfOnboardingProfileUpdateInput['workerInput']
    >({
      addressLine1: getUpdateInputValue('addressLine1'),
      addressLine2: getUpdateInputValue('addressLine2'),
      addressCity: getUpdateInputValue('addressCity'),
      addressState: getUpdateInputValue('addressState'),
      addressZipCode: getUpdateInputValue('addressZipCode'),
      primaryLanguage: getUpdateInputValue('primaryLanguage'),
      citizenshipStatus: getUpdateInputValue('citizenshipStatus'),
      workAuthorizationStatus: getUpdateInputValue('workAuthorizationStatus'),
      race: getUpdateInputValue('race'),
      gender: getUpdateInputValue('gender'),
      isVeteran: isVeteranInputValue,
    }),
    jobsiteWorkerInput: ensureNonUndefinedFields<
      SelfOnboardingProfileUpdateInput['jobsiteWorkerInput']
    >({
      trade: getUpdateInputValue('trade'),
      tradeExperience: getUpdateInputValue('tradeExperience'),
      jobTitle: getUpdateInputValue('jobTitle'),
      unionAffiliation: getUpdateInputValue('unionAffiliation'),
      classificationGroup: getUpdateInputValue('classificationGroup'),
      customWorkerData: jobsiteCustomWorkerDataFields.reduce(
        (acc, customField) => ({
          ...acc,
          // @ts-expect-error [function works, but typing dynamically-generated fields is annoying]
          [customField.fieldLabel]: getUpdateInputValue(customField.fieldLabel),
        }),
        {},
      ),
    }),
  };
};
